import { Edit3 } from "lucide-react";
import type { ReactNode } from "react";
import { useState } from "react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Separator } from "@/components/ui/separator";
import { loadSizes, materialTypes, serviceOptions, skipSizes, supplementalItems } from "@/lib/pricing";
import { formatCurrency } from "@/lib/utils";
import type { StepProps } from "@/components/booking/steps/types";
import { StepFrame } from "@/components/booking/steps/StepFrame";

export function ReviewStep({ state, isLocked, updateState, goNext, goBack, goToStep }: StepProps) {
  const [showTermsError, setShowTermsError] = useState(false);
  const service = serviceOptions.find((item) => item.id === state.service)?.name ?? "-";
  const selectedOption = skipSizes.find((item) => item.id === state.skipSize)?.title ?? loadSizes.find((item) => item.id === state.loadSize)?.title ?? materialTypes.find((item) => item.id === state.materialType)?.title ?? "-";

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        if (!state.acceptedTerms) {
          setShowTermsError(true);
          return;
        }
        goNext();
      }}
    >
      <StepFrame
        title="Review"
        description="Check the booking details before payment."
        canBack
        onBack={goBack}
        nextLabel="Continue to payment"
        nextDisabled={!state.acceptedTerms}
        isReadOnly={isLocked}
        mobileSummary={state.pricingResult ? `Total payable ${formatCurrency(state.pricingResult.totalPayable)}` : undefined}
      >
        <div className="grid gap-4 xl:grid-cols-2">
          <ReviewSection title="Booking" onEdit={() => goToStep(0)} rows={[["Postcode", state.postcode], ["Service", service], ["Size", selectedOption]]} isReadOnly={isLocked}>
            <Separator />
            <div>
              <div className="mb-3 flex items-center justify-between">
                <h4 className="text-sm font-black">Extra items</h4>
                {!isLocked ? (
                  <Button type="button" variant="ghost" size="sm" onClick={() => goToStep(2)}>
                    <Edit3 className="h-4 w-4" />
                    Edit
                  </Button>
                ) : null}
              </div>
              {supplementalItems.some((item) => state.supplementalItems[item.id] > 0) ? (
                supplementalItems.filter((item) => state.supplementalItems[item.id] > 0).map((item) => <Row key={item.id} label={item.title} value={`x ${state.supplementalItems[item.id]}`} />)
              ) : (
                <p className="text-sm text-muted-foreground">No extra items selected.</p>
              )}
            </div>
          </ReviewSection>
          <ReviewSection
            title="Customer and job"
            onEdit={() => goToStep(3)}
            isReadOnly={isLocked}
            rows={[
              ["Date", state.bookingDetails.date],
              ["Time slot", state.bookingDetails.timeSlot],
              ["Name", state.bookingDetails.fullName],
              ["Email", state.bookingDetails.email],
              ["Phone", state.bookingDetails.phone],
              ["Address", [state.bookingDetails.addressLine1, state.bookingDetails.addressLine2, state.bookingDetails.townCity, state.bookingDetails.county, state.bookingDetails.postcode].filter(Boolean).join(", ")],
              ["Company", state.bookingDetails.isCompanyBooking ? state.bookingDetails.companyName : "No"],
              ["Driver notes", state.bookingDetails.driverNotes || "None"],
            ]}
          />
        </div>
        {state.pricingResult ? (
          <Alert className="mt-4">
            <AlertTitle>Pricing breakdown</AlertTitle>
            <AlertDescription>
              <div className="mt-3 space-y-2">
                <Row label="Service price" value={formatCurrency(state.pricingResult.baseServicePrice)} />
                <Row label="Extra items" value={formatCurrency(state.pricingResult.supplementalItemsTotal)} />
                {state.discountResult ? (
                  <Row label="Discount" value={`-${formatCurrency(state.pricingResult.discount)}`} />
                ) : null}
                <Row label="VAT" value={formatCurrency(state.pricingResult.vat)} />
                <Separator />
                <Row label="Total payable" value={formatCurrency(state.pricingResult.totalPayable)} strong />
              </div>
            </AlertDescription>
          </Alert>
        ) : null}
        <section className="mt-4 rounded-lg border-2 border-primary/20 bg-primary/5 p-4">
          <div className="flex items-start gap-3">
            <Checkbox
              id="accept-terms"
              checked={state.acceptedTerms}
              onChange={(event) => {
                updateState({ acceptedTerms: event.target.checked });
                if (event.target.checked) {
                  setShowTermsError(false);
                }
              }}
              aria-describedby={showTermsError ? "accept-terms-error" : undefined}
              readOnly={isLocked}
              disabled={isLocked}
            />
            <div>
              <label htmlFor="accept-terms" className="text-sm font-bold leading-5">
                Accept our terms and conditions
              </label>
              {showTermsError ? (
                <p id="accept-terms-error" className="mt-1 text-sm font-semibold text-destructive">
                  You must accept the terms and conditions before continuing.
                </p>
              ) : null}
            </div>
          </div>
        </section>
      </StepFrame>
    </form>
  );
}

function ReviewSection({ title, rows, onEdit, isReadOnly, children }: { title: string; rows: string[][]; onEdit: () => void; isReadOnly: boolean; children?: ReactNode }) {
  return (
    <div className="rounded-lg border-2 border-accent/10 bg-white p-4">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="font-black">{title}</h3>
        {!isReadOnly ? (
          <Button type="button" variant="ghost" size="sm" onClick={onEdit}>
            <Edit3 className="h-4 w-4" />
            Edit
          </Button>
        ) : null}
      </div>
      <div className="space-y-2">
        {rows.map(([label, value]) => (
          <Row key={label} label={label} value={value || "-"} />
        ))}
        {children}
      </div>
    </div>
  );
}

function Row({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div className={strong ? "flex justify-between gap-4 text-base font-black" : "flex justify-between gap-4 text-sm"}>
      <span className="shrink-0 text-muted-foreground">{label}</span>
      <span className="min-w-0 break-words text-right font-bold text-foreground">{value}</span>
    </div>
  );
}
